import { useParams } from "react-router";
import ms from "ms";
import { useDispatch, useSelector } from "react-redux";
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";

import { useFetchQuoteListQuery, useFetchQuotesQuery } from "../services/api/quotesApi/quotes";
import {
  reset,
  resetFilter,
  resetPagination,
  resetSort,
  setFilterModel,
  setPaginationModel,
  setSortModel,
} from "../slices/quoteTableModels";
import { RootState } from "../store";
import { buildQuoteListParamsFromModels } from "../lib/quoteFilterParams";

/** Server-side paginated + filtered quotes list for the given status tab */
export function useQuotes(status: string) {
  const { organisationId } = useParams<{ organisationId?: string }>();
  const { filterModel, sortModel, paginationModel } = useQuoteTableModels();

  const params = buildQuoteListParamsFromModels(filterModel, sortModel, paginationModel, status);

  const api = useFetchQuoteListQuery({
    ...params,
    organisationId,
  }, {
    skip: !organisationId,
    pollingInterval: ms(`1m`),
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });

  return {
    ...api,
    quotes: api.data?.quotes ?? [],
    count: api.data?.count ?? 0,
    statusCounts: api.data?.statusCounts ?? {},
  };
}

/**
 * All quotes for the org, grouped by status. Loads the full set (no pagination),
 * so only use where the complete list is needed (e.g. the policy test resource picker).
 */
export function useAllQuotes() {
  const { organisationId } = useParams<{ organisationId?: string }>();

  const api = useFetchQuotesQuery({ organisationId }, {
    skip: !organisationId,
    pollingInterval: ms(`1m`),
    skipPollingIfUnfocused: true,
  });

  return {
    ...api,
    quotes: api.data?.quotes ?? {},
  };
}

export function useQuoteTableModels() {
  const dispatch = useDispatch();
  const quoteTableModels = useSelector((s: RootState) => s.quoteTableModels);

  return {
    // State
    filterModel: quoteTableModels.filterModel,
    sortModel: quoteTableModels.sortModel,
    paginationModel: quoteTableModels.paginationModel,

    // Actions
    setFilterModel: (model: GridFilterModel) => dispatch(setFilterModel(model)),
    setSortModel: (model: GridSortModel) => dispatch(setSortModel(model)),
    setPaginationModel: (model: GridPaginationModel) => dispatch(setPaginationModel(model)),
    resetModels: () => dispatch(reset()),
    resetSort: () => dispatch(resetSort()),
    resetFilter: () => dispatch(resetFilter()),
    resetPagination: () => dispatch(resetPagination()),
  };
}
